<template>
  <div
    v-if="display"
    class="fade-away-message-container rounded-lg elevation-1"
    :class="{
      show: showClass,
      hide: !showClass,
      variation1: showVariation1Class,
      variation2: showVariation2Class,
      variation3: showVariation3Class,
      variation4: showVariation4Class,
      variation5: showVariation5Class,
    }"
    :style="{
      top: isMobile ? top : top + '%',
      bottom: isMobile ? '16px' : 'auto',
      minHeight: header && message ? '70px' : '56px',
      maxWidth: isMobile ? 'calc(100% - 32px)' : width + 'px',
      width: isMobile ? 'calc(100% - 32px)' : width + 'px',
    }"
  >
    <!-- Variation 2: left colored line + circle icon -->
    <div
      class="icon-container pa-2 d-flex align-center"
      v-if="displayType === 'variation2'"
    >
      <div class="left-line" :class="background"></div>
      <v-icon :class="background" class="icon variation2-icon">{{
        mdiIcon
      }}</v-icon>
    </div>

    <!-- Default icon block -->
    <div
      class="icon-container d-flex align-center justify-center"
      :class="background"
      v-else
    >
      <v-icon class="icon">{{ mdiIcon }}</v-icon>
    </div>

    <!-- Message -->
    <div class="message-container pa-3 d-flex align-center">
      <div class="title-message-wrapper">
        <div :class="headerClasses" v-if="header">{{ header }}</div>
        <div
          class="message"
          style="font-family: 'Arimo', sans-serif; color: black;"
          v-if="message"
          :style="isMobile ? 'font-size: 10px' : 'font-size:14px'"
        >
          {{ message }}
        </div>
      </div>
    </div>

    <!-- Close -->
    <div class="close-container d-flex align-center justify-center">
      <v-btn icon small text @click="clickClose">
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "FadeAwayMessage",

  props: {
    value: { type: Boolean, default: true },
    header: { type: String, default: "SERVER ERROR" },
    message: { type: String, default: "Something went wrong!" },
    timeout: { type: [Number, String], default: 1500 },
    type: { type: String, default: "success" },
    top: { type: [Number, String], default: 20 },
    backgroundClass: { type: String, default: "primary" },
    textColorClass: { type: String, default: "white--text" },
    displayType: { type: String, default: "default" },
    width: { type: [Number, String], default: 600 },
  },

  data: () => ({
    display: false,
    mdiIcon: "mdi-check",
    background: "primary",
    headerClasses: "msg-title",
    isMobile: false,

    showClass: false,
    showVariation1Class: false,
    showVariation2Class: false,
    showVariation3Class: false,
    showVariation4Class: false,
    showVariation5Class: false,
  }),

  watch: {
    type(val) {
      this.setType(val);
    },
    value(val) {
      this.display = val;
    },
    display(val) {
      val ? this.showBox() : this.clickClose();
    },
  },

  mounted() {
    this.display = this.value;
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
    document.body.appendChild(this.$el);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },

  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 600;
    },

    showBox() {
      this.$emit("input", true);
      this.headerClasses = "msg-title";
      if (this.message && this.header) this.headerClasses += " mb-1";

      this.setType(this.type); // ✅ fixed: pass this.type
      this.setMainContainerClass();

      setTimeout(() => {
        this.showClass = true;
        setTimeout(() => this.clickClose(), this.timeout);
      }, 10);
    },

    clickClose() {
      this.showClass = false;
      setTimeout(() => {
        this.display = false;
        this.$emit("input", false);
      }, 400);
    },

    setMainContainerClass() {
      this.showVariation1Class = false;
      this.showVariation2Class = false;
      this.showVariation3Class = false;
      this.showVariation4Class = false;
      this.showVariation5Class = false;

      const map = {
        variation1: "showVariation1Class",
        variation2: "showVariation2Class",
        variation3: "showVariation3Class",
        variation4: "showVariation4Class",
        variation5: "showVariation5Class",
      };
      if (map[this.displayType]) this[map[this.displayType]] = true;
    },

    setType(type) {
      const types = {
        success: { bg: "primary", icon: "mdi-check" },
        primary: { bg: "primary", icon: "mdi-check" },
        warning: { bg: "amber", icon: "mdi-alert-circle-outline" },
        danger: { bg: "error", icon: "mdi-alert-octagon-outline" },
        error: { bg: "error", icon: "mdi-alert-octagon-outline" },
        info: { bg: "primary", icon: "mdi-information-slab-circle-outline" },
      };
      const t = types[type] || types.success;
      this.background = t.bg;
      this.mdiIcon = t.icon;
    },
  },
};
</script>

<style>
/* Non-scoped so styles reach the body-appended element */

.fade-away-message-container {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  background-color: #fefefe;
  border: 1px solid #e0e0e0;
  border-radius: 10px !important;
  z-index: 9999;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  opacity: 0;
  transition: opacity 0.35s ease, bottom 0.35s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.fade-away-message-container.show {
  opacity: 1;
}
.fade-away-message-container.hide {
  opacity: 0;
}

/* ── Icon column ── */
.fade-away-message-container .icon-container {
  flex-shrink: 0;
  width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fade-away-message-container .icon {
  font-size: 26px;
  color: #fff !important;
}

/* ── Message column ── */
.fade-away-message-container .message-container {
  flex: 1;
  min-width: 0;
}

.fade-away-message-container .title-message-wrapper {
  display: flex;
  flex-direction: column;
}

.fade-away-message-container .msg-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0;
  font-family: "Arimo", sans-serif;
  line-height: 1.4;
}

.fade-away-message-container .message {
  font-size: 13px;
  font-weight: 500;
  color: #444;
  line-height: 1.4;
  word-break: break-word;
  white-space: normal;
}

/* ── Close column ── */
.fade-away-message-container .close-container {
  flex-shrink: 0;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fade-away-message-container .close-container button i {
  color: #aaa !important;
}

/* ── Color classes ── */
.fade-away-message-container .primary {
  background-color: #1976d2 !important;
  color: #fff !important;
}
.fade-away-message-container .error {
  background-color: #f44336 !important;
  color: #fff !important;
}
.fade-away-message-container .amber {
  background-color: #ffc107 !important;
  color: #fff !important;
}

/* ── Variation 1 ── */
.fade-away-message-container.variation1 .icon-container {
  width: 60px;
}
.fade-away-message-container.variation1 .icon {
  font-size: 28px;
}
.fade-away-message-container.variation1 .message-container {
  padding: 0 12px;
}
.fade-away-message-container.variation1 .message {
  font-weight: 300;
  font-size: 12px;
  letter-spacing: 0.6px;
  color: #959595;
}
.fade-away-message-container.variation1 .close-container button {
  background-color: #fff !important;
}

/* ── Variation 2 ── */
.fade-away-message-container.variation2 {
  padding: 0;
}

.fade-away-message-container.variation2 .icon-container {
  padding: 0 !important;
  width: auto;
  display: flex;
  align-items: center;
}

.fade-away-message-container .left-line {
  width: 5px;
  align-self: stretch;
  flex-shrink: 0;
}

.fade-away-message-container.variation2 .variation2-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 20px !important;
  color: #fff !important;
  margin: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fade-away-message-container.variation2 .message-container {
  padding: 10px 8px;
}
.fade-away-message-container.variation2 .message {
  font-weight: 300;
  font-size: 12px;
  letter-spacing: 0.6px;
  color: #959595;
}
.fade-away-message-container.variation2 .close-container button {
  background-color: #fff !important;
}

/* ── Mobile (≤ 600px) ── */
@media (max-width: 600px) {
  .fade-away-message-container {
    left: 16px !important;
    right: 16px !important;
    transform: none !important;
    border-radius: 12px !important;
  }

  .fade-away-message-container .icon-container {
    width: 48px;
  }
  .fade-away-message-container .icon {
    font-size: 22px;
  }
  .fade-away-message-container .close-container {
    width: 40px;
  }
  .fade-away-message-container .msg-title {
    font-size: 12px;
  }
  .fade-away-message-container .message {
    font-size: 12px;
  }

  .fade-away-message-container.variation1 .icon-container {
    width: 48px;
  }
  .fade-away-message-container.variation2 .variation2-icon {
    width: 28px;
    height: 28px;
    font-size: 16px !important;
    margin: 0 8px;
  }
}
</style>
